import { useCallback, useMemo } from 'react'

import { Box, Button, Card, Center, Flex, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import {
  IconAlertTriangle,
  IconArrowNarrowLeft,
  IconCheck,
  IconEye,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react'
import { Title as RATitle, useTranslate } from 'react-admin'
import { useNavigate, useParams } from 'react-router-dom'
import { useDeleteApiRolesId, useGetApiRolesId } from '../../../codegen/generated/roles'
import { LXLoader } from '../../../components/LXLoader'
import { useUserStore } from '../../../stores/useUserStore'
import { InformationsRole } from './InformationsRole'
import { UpdateRole } from './UpdateRole'

type RoleProps = {
  isEdit?: boolean
}

export const Role = ({ isEdit = false }: RoleProps) => {
  const { roleId } = useParams()
  const t = useTranslate()
  const navigate = useNavigate()
  const userStore = useUserStore()

  const roleQuery = useGetApiRolesId(roleId ?? '', {
    query: { enabled: !!roleId },
  })
  const deleteRoleMutation = useDeleteApiRolesId()

  const role = useMemo(() => roleQuery.data?.body, [roleQuery.data?.body])

  const title = useMemo(
    () =>
      `${t('pages.users.roles')} / ${role?.name ?? ''} ${isEdit ? `/ ${t('_generics.edit')}` : ''}`,
    [t, role, isEdit],
  )

  const onUpdate = useCallback(() => {
    roleQuery.refetch()

    showNotification({
      color: 'green',
      icon: <IconCheck />,
      title: t('pages.users.success.roleUpdate.title'),
      message: t('pages.users.success.roleUpdate.message'),
    })
  }, [roleQuery.refetch, t])

  const onDelete = useCallback(
    () =>
      modals.openConfirmModal({
        title: (
          <Text fw="bold" size="h3">
            {t('pages.users.deleteRole')}
          </Text>
        ),
        children: (
          <Center>
            <Flex align="center" my="md">
              <IconAlertTriangle color="red" alignmentBaseline="baseline" />
              <Text ml="sm" c="red" fw="bold">
                {t('pages.users.confirm.wantToDeleteRole')}
              </Text>
            </Flex>
          </Center>
        ),
        labels: {
          confirm: t('_generics.delete'),
          cancel: t('_generics.cancel'),
        },
        cancelProps: { variant: 'subtle' },
        confirmProps: { variant: 'filled', leftSection: <IconTrash size={16} />, color: 'red' },
        onConfirm: async () => {
          try {
            if (role?.id) {
              await deleteRoleMutation.mutateAsync({
                id: role?.id,
              })

              navigate('/users-roles/roles/')

              showNotification({
                color: 'green',
                icon: <IconCheck />,
                title: t('pages.users.success.roleDelete.title'),
                message: t('pages.users.success.roleDelete.message'),
              })
            }
          } catch (e) {
            console.error(e)
            // showErrorNotification(e)
          }
        },
      }),
    [deleteRoleMutation, navigate, role?.id, t],
  )

  if (roleQuery.isLoading) {
    return (
      <Box w="100%">
        <LXLoader />
      </Box>
    )
  }

  return (
    <Box w="100%">
      <RATitle title={title} />
      <Flex justify="space-between" align="center">
        <Button
          type="button"
          variant="subtle"
          size="compact-sm"
          leftSection={<IconArrowNarrowLeft size={16} />}
          onClick={() => navigate('/users-roles/roles/')}
        >
          <Text size="sm" tt="uppercase">
            {t('_generics.backToList')}
          </Text>
        </Button>

        {role?.isReadonly === false && (
          <Box>
            {isEdit === true ? (
              <Button
                type="button"
                variant="subtle"
                size="compact-sm"
                leftSection={<IconEye size={16} />}
                onClick={() => navigate(`/users-roles/roles/${roleId}`)}
              >
                <Text size="sm" tt="uppercase">
                  {t('_generics.display')}
                </Text>
              </Button>
            ) : (
              <>
                <Tooltip
                  label={`⚠ ${t('pages.users.lackingPermission')} → ${t(
                    'pages.users.permissionsList.roles.edit',
                  )}`}
                  hidden={userStore.isAuthorised('roles.edit')}
                >
                  <span>
                    <Button
                      type="button"
                      variant="subtle"
                      size="compact-sm"
                      leftSection={<IconPencil size={16} />}
                      onClick={() => {
                        navigate(`/users-roles/roles/${roleId}/edit`)
                      }}
                      disabled={!userStore.isAuthorised('roles.edit')}
                    >
                      <Text size="sm" tt="uppercase">
                        {t('_generics.edit')}
                      </Text>
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  type="button"
                  variant="subtle"
                  size="compact-sm"
                  leftSection={<IconTrash size={16} />}
                  color="red"
                  onClick={onDelete}
                  disabled={!userStore.isAuthorised('roles.edit')}
                >
                  <Text size="sm" tt="uppercase">
                    {t('_generics.delete')}
                  </Text>
                </Button>
              </>
            )}
          </Box>
        )}
      </Flex>
      <Card shadow="xs" p="xl">
        {isEdit ? <UpdateRole role={role} onUpdate={onUpdate} /> : <InformationsRole role={role} />}
      </Card>
    </Box>
  )
}
